import React, { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { VendorAuthProvider } from "./context/VendorAuthContext";
import { Toaster } from "sonner";
import Login from "./pages/Login";
import AppShell from "./layout/AppShell";
import Dashboard from "./pages/Dashboard";
import InstallPWA from "./components/InstallPWA";

// Lazy-load heavy pages so first paint and the Dashboard stay snappy.
// Orders.jsx (~2100 LOC) and Logistics.jsx (~2100 LOC) alone push the main
// bundle past 500 KB; code-splitting them cuts the initial JS by roughly
// half on this route.
const Orders = lazy(() => import("./pages/Orders"));
const Logistics = lazy(() => import("./pages/Logistics"));
const Finance = lazy(() => import("./pages/Finance"));
const Calendar = lazy(() => import("./pages/Calendar"));
const Users = lazy(() => import("./pages/Users"));
const JoinForm = lazy(() => import("./pages/JoinForm"));
const Customers = lazy(() => import("./pages/Customers"));
const Vendors = lazy(() => import("./pages/Vendors"));
const Workbook = lazy(() => import("./pages/Workbook"));
const Labour = lazy(() => import("./pages/Labour"));
const Laundry = lazy(() => import("./pages/Laundry"));
const Repairs = lazy(() => import("./pages/Repairs"));
const CashPayments = lazy(() => import("./pages/CashPayments"));
const Inventory = lazy(() => import("./pages/Inventory"));
const Track = lazy(() => import("./pages/Track"));
const ClientDashboard = lazy(() => import("./pages/ClientDashboard"));
const CustomerLogin = lazy(() => import("./pages/CustomerLogin"));
const CustomerDashboard = lazy(() => import("./pages/CustomerDashboard"));
const Photos = lazy(() => import("./pages/Photos"));
const Themes = lazy(() => import("./pages/Themes"));
const VendorLogin = lazy(() => import("./pages/VendorLogin"));
const VendorDashboard = lazy(() => import("./pages/VendorDashboard"));
const Procurement = lazy(() => import("./pages/procurement/ProcurementPage"));
const Billing = lazy(() => import("./pages/Billing"));

function PageLoader() {
  return (
    <div className="min-h-[40vh] flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-red-600 border-t-transparent animate-spin" />
    </div>
  );
}

// Warm the page bundles one by one in idle time after login so tab switches don't wait on a download.
const PRELOAD = [
  () => import("./pages/Orders"), () => import("./pages/Billing"), () => import("./pages/Users"),
  () => import("./pages/Repairs"), () => import("./pages/procurement/ProcurementPage"), () => import("./pages/Laundry"),
  () => import("./pages/Finance"), () => import("./pages/Calendar"), () => import("./pages/Logistics"),
  () => import("./pages/Customers"), () => import("./pages/Inventory"), () => import("./pages/CashPayments"),
];
function usePreloadPages(active) {
  React.useEffect(() => {
    if (!active) return undefined;
    const idle = (cb) => (window.requestIdleCallback ? window.requestIdleCallback(cb, { timeout: 3000 }) : setTimeout(cb, 400));
    let i = 0;
    let stop = false;
    const next = () => { if (stop || i >= PRELOAD.length) return; PRELOAD[i++]().catch(() => {}).finally(() => idle(next)); };
    const t = setTimeout(() => idle(next), 2500);
    return () => { stop = true; clearTimeout(t); };
  }, [active]);
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  usePreloadPages(!!user);
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="h-10 w-10 rounded-full border-2 border-red-600 border-t-transparent animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/" replace />;
  return children;
}

// Round 59 — admin-only route guard. Staff hitting Finance/Labour/
// Workbook directly via URL bounce back to the dashboard. Sidebar
// already hides these tabs; this is the belt-and-braces server-equiv
// at the routing layer.
function AdminOnly({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.mobile === "9243425500";
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

// Round 95 — Allow direct URL access for any of the listed roles.
// Admin is always allowed. Root admin mobile bypass retained.
const hasPage = (user, page) => !!page && Array.isArray(user?.pages) && user.pages.includes(page);

function RoleGuard({ roles, page, children }) {
  const { user } = useAuth();
  const role = user?.role || "staff";
  const rootAdmin = user?.mobile === "9243425500";
  const ok = rootAdmin || role === "admin" || (Array.isArray(user?.pages) ? hasPage(user, page) : (roles || []).includes(role));
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

// Round 122 — Staff-only gate (also admits admin). Used for Customers,
// Photos, Vendors — per user's RBAC spec these are staff-facing (not
// exposed to supervisors).
// Round 123.3 — Also admit `manager` (the actual RBAC role that gets
// Customers/Photos/Vendors in the sidebar; supervisors do NOT).
// Previously managers clicked a nav link and were bounced to the
// dashboard by this guard.
function StaffOrAdmin({ page, children }) {
  const { user } = useAuth();
  const role = user?.role || "staff";
  const rootAdmin = user?.mobile === "9243425500";
  const ok = rootAdmin || role === "admin" || (Array.isArray(user?.pages) ? hasPage(user, page) : role === "manager" || role === "staff" || role === "accountant");
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

// Round 126.30 — Billing module gate. Admin + accountant get full
// write access; manager gets read-only (handled inside the page);
// everyone else is bounced.
function BillingGuard({ children }) {
  const { user } = useAuth();
  const role = user?.role || "staff";
  const rootAdmin = user?.mobile === "9243425500";
  const ok = rootAdmin || role === "admin" || (Array.isArray(user?.pages) ? hasPage(user, "billing") : role === "accountant" || role === "manager" || role === "staff" || !!user?.billing_access);
  if (!ok) return <Navigate to="/" replace />;
  return children;
}

// HR role only uses the Team page.
function HrHome({ children }) {
  const { user } = useAuth();
  const teamOnly = Array.isArray(user?.pages) ? !user.pages.includes("dashboard") : ["hr", "employee"].includes(user?.role);
  if (teamOnly && user?.mobile !== "9243425500" && user?.role !== "admin") return <Navigate to="/users" replace />;
  return children;
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <VendorAuthProvider>
        <Toaster position="top-right" richColors closeButton />
        <InstallPWA />
        <BrowserRouter>
          <Routes>
            <Route path="/join/:token" element={<Suspense fallback={<PageLoader />}><JoinForm /></Suspense>} />
            <Route
              path="/login"
              element={
                <PublicOnly>
                  <Login />
                </PublicOnly>
              }
            />
            {/* Round 64 — Vendor Portal (separate auth, separate localStorage) */}
            <Route
              path="/vendor/login"
              element={
                <Suspense fallback={<PageLoader />}>
                  <VendorLogin />
                </Suspense>
              }
            />
            <Route
              path="/vendor"
              element={
                <Suspense fallback={<PageLoader />}>
                  <VendorDashboard />
                </Suspense>
              }
            />
            {/* Round 68 — Customer Portal */}
            <Route path="/customer/login" element={<Suspense fallback={<PageLoader />}><CustomerLogin /></Suspense>} />
            <Route path="/customer" element={<Suspense fallback={<PageLoader />}><CustomerDashboard /></Suspense>} />
            {/* Public unauthenticated tracking page (linked from challan QR) */}
            <Route
              path="/track/:id"
              element={
                <Suspense fallback={<PageLoader />}>
                  <Track />
                </Suspense>
              }
            />
            {/* Round 61 — Premium client-facing event dashboard (Share button on every order) */}
            <Route
              path="/client/:id"
              element={
                <Suspense fallback={<PageLoader />}>
                  <ClientDashboard />
                </Suspense>
              }
            />
            <Route
              path="/"
              element={
                <Protected>
                  <AppShell />
                </Protected>
              }
            >
              <Route
                index
                element={
                  <HrHome>
                    <Suspense fallback={<PageLoader />}>
                      <Dashboard />
                    </Suspense>
                  </HrHome>
                }
              />
              <Route
                path="orders"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Orders />
                  </Suspense>
                }
              />
              <Route
                path="logistics"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Logistics />
                  </Suspense>
                }
              />
              <Route
                path="calendar"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Calendar />
                  </Suspense>
                }
              />
              <Route
                path="customers"
                element={
                  <StaffOrAdmin page="customers">
                    <Suspense fallback={<PageLoader />}>
                      <Customers />
                    </Suspense>
                  </StaffOrAdmin>
                }
              />
              <Route
                path="photos"
                element={
                  <StaffOrAdmin page="photos">
                    <Suspense fallback={<PageLoader />}>
                      <Photos />
                    </Suspense>
                  </StaffOrAdmin>
                }
              />
              <Route
                path="themes"
                element={
                  <AdminOnly>
                    <Suspense fallback={<PageLoader />}>
                      <Themes />
                    </Suspense>
                  </AdminOnly>
                }
              />
              <Route
                path="finance"
                element={
                  <AdminOnly>
                    <Suspense fallback={<PageLoader />}>
                      <Finance />
                    </Suspense>
                  </AdminOnly>
                }
              />
              <Route
                path="procurement"
                element={
                  <StaffOrAdmin page="procurement">
                    <Suspense fallback={<PageLoader />}>
                      <Procurement />
                    </Suspense>
                  </StaffOrAdmin>
                }
              />
              <Route
                path="vendors"
                element={
                  <StaffOrAdmin page="vendors">
                    <Suspense fallback={<PageLoader />}>
                      <Vendors />
                    </Suspense>
                  </StaffOrAdmin>
                }
              />
              <Route
                path="billing"
                element={
                  <BillingGuard>
                    <Suspense fallback={<PageLoader />}>
                      <Billing />
                    </Suspense>
                  </BillingGuard>
                }
              />
              <Route
                path="workbook"
                element={
                  <AdminOnly>
                    <Suspense fallback={<PageLoader />}>
                      <Workbook />
                    </Suspense>
                  </AdminOnly>
                }
              />
              <Route
                path="labour"
                element={
                  <RoleGuard roles={["supervisor"]} page="labour">
                    <Suspense fallback={<PageLoader />}>
                      <Labour />
                    </Suspense>
                  </RoleGuard>
                }
              />
              <Route
                path="repairs"
                element={
                  <RoleGuard roles={["supervisor"]} page="repairs">
                    <Suspense fallback={<PageLoader />}>
                      <Repairs />
                    </Suspense>
                  </RoleGuard>
                }
              />
              <Route
                path="laundry"
                element={
                  <RoleGuard roles={["supervisor"]} page="laundry">
                    <Suspense fallback={<PageLoader />}>
                      <Laundry />
                    </Suspense>
                  </RoleGuard>
                }
              />
              <Route
                path="cash-payments"
                element={
                  <RoleGuard roles={["supervisor"]} page="cashPayments">
                    <Suspense fallback={<PageLoader />}>
                      <CashPayments />
                    </Suspense>
                  </RoleGuard>
                }
              />
              <Route
                path="inventory/*"
                element={
                  <RoleGuard roles={["supervisor"]} page="inventory">
                    <Suspense fallback={<PageLoader />}>
                      <Inventory />
                    </Suspense>
                  </RoleGuard>
                }
              />
              <Route
                path="users"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <Users />
                  </Suspense>
                }
              />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        </VendorAuthProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
